import { orgSelectors } from 'ducks/orgs'
import { ComponentLikeType, DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import { useGlobalCart } from 'hooks/useGlobalCart'
import { useAddedToCartToast } from 'pages/ordering/addedToCartToast'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { computeBundleDiscount, getBundleComponentCodes } from '../dealEngine'
import { Deal } from '../types'

const restClientInstance = restClient(window.API_ROOT + '/api')

interface UseBundleDealOptions {
  deal: Deal
  currentProductCode?: string
}

export interface UseBundleDealResult {
  isFetching: boolean
  bundleCodes: string[]
  bundleProducts: ComponentLikeType[]
  sortedProducts: ComponentLikeType[]
  productsToAdd: ComponentLikeType[]
  selectedProducts: Set<string>
  toggleProduct: (code: string) => void
  isIncludesAll: boolean
  allowSelection: boolean
  otherLabel: 'Required' | 'Recommended'
  hasOutOfStock: boolean
  bundleTotal: number
  savings: number
  discountedTotal: number
  isItemInCart: (code: string) => boolean
  addProductsToCart: (products: ComponentLikeType[]) => void
  allInCart: boolean
  addBundleToCart: () => void
}

/**
 * Bundle deal hook for `BundleSection` and `BundleDealModal`: fetches products,
 * selection, pricing totals, and global-cart add/check helpers.
 */
export const useBundleDeal = ({ deal, currentProductCode }: UseBundleDealOptions): UseBundleDealResult => {
  const [isFetching, setIsFetching] = useState(false)
  const [bundleProducts, setBundleProducts] = useState<ComponentLikeType[]>([])
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set())

  const { addLineItemFromV2Component, orderableLineItems } = useGlobalCart()
  const { showAddedToCartToast } = useAddedToCartToast()

  const bundleCodes = useMemo(() => getBundleComponentCodes(deal).map((code) => code.toString()), [deal])

  const isIncludesAll = useMemo(() => deal.conditions?.some((c) => c.operator === 'includes_all') ?? false, [
    deal.conditions,
  ])

  const allowSelection = useMemo(
    () => deal.conditions?.some((c) => ['includes_any', 'in'].includes(c.operator)) ?? false,
    [deal.conditions]
  )

  const otherLabel: 'Required' | 'Recommended' = isIncludesAll ? 'Required' : 'Recommended'

  const orgId = useSelector(orgSelectors.getOrg)?.id
  const distributorKey = deal.distributor

  useEffect(() => {
    if (!bundleCodes.length || !orgId || !distributorKey) return

    let aborted = false
    setIsFetching(true)
    const params = new URLSearchParams({
      filter: bundleCodes.join(','),
      limit: bundleCodes.length.toString(),
      show_archived: '0',
      match_parents: '1',
      distributors: JSON.stringify([distributorKey]),
    }).toString()

    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/componentsv2/?${params}`,
    })
      .then((response: { data: ComponentLikeType[] }) => {
        if (aborted) return
        setBundleProducts(response.data)
        setSelectedProducts(new Set(response.data.map((p) => p.code)))
      })
      .catch((error: unknown) => {
        if (!aborted) console.error('Error fetching bundle products', error)
      })
      .finally(() => {
        if (!aborted) setIsFetching(false)
      })

    return () => {
      aborted = true
    }
  }, [bundleCodes, orgId, distributorKey])

  const sortedProducts = useMemo(() => {
    if (!currentProductCode) return bundleProducts
    return [...bundleProducts].sort((a, b) => {
      if (a.code === currentProductCode) return -1
      if (b.code === currentProductCode) return 1
      return 0
    })
  }, [bundleProducts, currentProductCode])

  const productsToAdd = useMemo(
    () => (allowSelection ? sortedProducts.filter((p) => selectedProducts.has(p.code)) : sortedProducts),
    [allowSelection, sortedProducts, selectedProducts]
  )

  const toggleProduct = useCallback((code: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }, [])

  const hasOutOfStock = useMemo(
    () =>
      productsToAdd.some((product) => {
        const entry = product.ordering_v2?.[0]
        if (!entry) return true
        const totalStock = (entry.stock_levels || []).reduce((sum: number, sl) => sum + (sl.quantity || 0), 0)
        return totalStock <= 0
      }),
    [productsToAdd]
  )

  const bundleTotal = useMemo(() => {
    let total = 0
    for (const product of productsToAdd) {
      const entry = product.ordering_v2?.[0]
      if (entry?.price_detail?.price_ex_vat) {
        total += entry.price_detail.price_ex_vat
      }
    }
    return total
  }, [productsToAdd])

  const savings = useMemo(() => computeBundleDiscount(bundleTotal, deal), [bundleTotal, deal])
  const discountedTotal = bundleTotal - savings

  const cartCodes = useMemo(() => new Set(orderableLineItems.map((li) => li.code)), [orderableLineItems])

  const isItemInCart = useCallback((code: string) => cartCodes.has(code), [cartCodes])

  const addProductsToCart = useCallback(
    (products: ComponentLikeType[]) => {
      const addedProducts: ComponentLikeType[] = []
      for (const product of products) {
        if (cartCodes.has(product.code)) continue
        const distributorEntry = product.ordering_v2?.[0]
        if (distributorEntry) {
          addLineItemFromV2Component(product, distributorEntry as DistributorDataTypeV2)
          addedProducts.push(product)
        }
      }

      if (!addedProducts.length) return

      const toastProducts = currentProductCode
        ? [...addedProducts].sort((a, b) => {
            if (a.code === currentProductCode) return -1
            if (b.code === currentProductCode) return 1
            return 0
          })
        : addedProducts

      for (const product of toastProducts) {
        const distributorEntry = product.ordering_v2?.[0]
        if (!distributorEntry) continue
        const unitPrice = distributorEntry.price_detail?.price_ex_vat ?? distributorEntry.price ?? 0
        showAddedToCartToast({
          variant: 'item',
          item: {
            description: distributorEntry.short_description ?? product.name,
            code: product.code,
            imageUrl:
              distributorEntry.image_url ||
              product.ordering_v2?.find((entry) => entry.image_url)?.image_url,
            quantity: 1,
            price: unitPrice,
            distributor: distributorEntry.distributor,
          },
        })
      }
    },
    [cartCodes, addLineItemFromV2Component, currentProductCode, showAddedToCartToast]
  )

  const allInCart = useMemo(
    () => productsToAdd.length > 0 && productsToAdd.every((p) => cartCodes.has(p.code)),
    [productsToAdd, cartCodes]
  )

  const addBundleToCart = useCallback(() => {
    if (allInCart) return
    addProductsToCart(productsToAdd)
  }, [allInCart, addProductsToCart, productsToAdd])

  return {
    isFetching,
    bundleCodes,
    bundleProducts,
    sortedProducts,
    productsToAdd,
    selectedProducts,
    toggleProduct,
    isIncludesAll,
    allowSelection,
    otherLabel,
    hasOutOfStock,
    bundleTotal,
    savings,
    discountedTotal,
    isItemInCart,
    addProductsToCart,
    allInCart,
    addBundleToCart,
  }
}
